import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

interface ClosedTradeItemProps {
  data: {
    close_time: string
    symbol: string
    side: 'buy' | 'sell' | string
    volume: number
    net_profit: number
    commission: number
    swap: number
    profit_gross: number
    balance_after: number
    pips: number
    history_id?: string
  }
}

const ClosedTradeItem = (props: ClosedTradeItemProps) => {

  const { data } = props
  const { t } = useTranslation();

  const [getDate, setGetDate] = useState<string>('')
  const [getTime, setGetTime] = useState<string>('')

  function formatFullDate(input: string) {
    const date = new Date(input);

    if (isNaN(date.getTime())) return "";

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    setGetDate(`${day} ${month} ${year}`);
    setGetTime(`${hours}:${minutes}:${seconds}`)
  }

  function secondsToHHMMSS(totalSeconds: number): string {
    if (isNaN(totalSeconds) || totalSeconds < 0) return "00:00:00";

    const total = Math.floor(totalSeconds);

    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    const pad = (n: number) => n.toString().padStart(2, "0");
    const timePart = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

    return days > 0 ? `${days}d ${timePart}` : timePart;
  }

  useEffect(() => {
    formatFullDate(data.close_time)
  }, [data])

  return (
    <tr>
      <td style={{ width: '160px', minWidth: '160px' }}>
        <div className="text-labels-medium text-content-primary">
          {getDate}<br />
          {getTime}</div>
      </td>
      <td style={{ width: '140px', minWidth: '120px' }}>
        <div className="text-labels-medium text-content-primary">{data.symbol}</div>
      </td>
      <td style={{ width: '100px', minWidth: '90px' }}>
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-labels-small ${['mua', 'buy'].includes(String(data.side).toLowerCase())
            ? 'bg-green-100 text-functional-success'
            : 'bg-red-100 text-functional-danger'
          }`}>
          {['mua', 'buy'].includes(String(data.side).toLowerCase()) ? t('Buy') : t('Sell')}
        </div>
      </td>
      <td style={{ width: '100px', minWidth: '90px' }}>
        <div className="text-labels-medium text-content-primary">{Number(data.volume).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </td>
      <td style={{ width: '140px', minWidth: '120px' }}>
        <div className={`text-labels-medium ${data.net_profit < 0 ? 'text-functional-danger' : 'text-functional-success'}`}>{data.net_profit < 0 ? '-' : ''}${Number(Math.abs(data.net_profit)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{data.net_profit < 0 ? '' : ''}</div>
      </td>
      {/* <td style={{ width: '120px', minWidth: '100px' }}>
        <div className="text-labels-medium text-content-primary">${Number(data.commission).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </td>
      <td style={{ width: '120px', minWidth: '100px' }}>
        <div className="text-labels-medium text-content-primary">${Number(data.swap).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </td> */}
      <td style={{ width: '140px', minWidth: '120px' }}>
        <div className={`text-labels-medium ${data.profit_gross < 0 ? 'text-functional-danger' : 'text-functional-success'}`}>{data.profit_gross < 0 ? '-' : ''}${Number(Math.abs(data.profit_gross)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </td>
      <td style={{ width: '160px', minWidth: '140px' }}>
        <div className="text-labels-medium text-content-primary">${Number(data.balance_after).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </td>
      <td style={{ width: '100px', minWidth: '90px' }}>
        <div className={`${data.pips < 0 ? 'text-functional-danger' : 'text-functional-success'} text-labels-medium`}>{Number(data.pips).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </td>
    </tr>
  )
}

export default ClosedTradeItem

const formatMoney = (value: number) =>
  `${value < 0 ? '-' : ''}$${Math.abs(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// Mobile version of the journal row: everything important in two short lines
export const ClosedTradeCard = ({ data }: ClosedTradeItemProps) => {
  const { t } = useTranslation();
  const isBuy = ['mua', 'buy'].includes(String(data.side).toLowerCase());
  const closeDate = new Date(data.close_time);
  const closeText = isNaN(closeDate.getTime())
    ? ''
    : `${closeDate.getDate()} ${closeDate.toLocaleString('en-US', { month: 'short' })} ${String(closeDate.getHours()).padStart(2, '0')}:${String(closeDate.getMinutes()).padStart(2, '0')}`;
  const pips = Number(data.pips) || 0;

  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${isBuy ? 'bg-green-100 text-functional-success' : 'bg-red-100 text-functional-danger'}`}>
            {isBuy ? t('Buy') : t('Sell')}
          </span>
          <span className="text-sm font-bold text-content-primary truncate">{data.symbol}</span>
          <span className="shrink-0 text-xs text-content-tertiary">{Number(data.volume).toLocaleString('en-US', { maximumFractionDigits: 2 })} lot</span>
        </div>
        <span className={`shrink-0 text-sm font-bold ${data.net_profit < 0 ? 'text-functional-danger' : 'text-functional-success'}`}>
          {data.net_profit > 0 ? '+' : ''}{formatMoney(data.net_profit)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1 text-[11px] text-content-tertiary">
        <span>
          {closeText}
          {' · '}
          <span className={pips < 0 ? 'text-functional-danger' : 'text-functional-success'}>{pips > 0 ? '+' : ''}{pips.toFixed(1)} pip</span>
        </span>
        <span className="shrink-0">{t('BalanceAfter')}: {formatMoney(data.balance_after)}</span>
      </div>
    </div>
  )
}
